import { User } from 'firebase/auth';

export type Complexity = 'basic' | 'intermediate' | 'advanced';

export interface KidsUserProfile {
  userId: string;
  email: string;
  age?: number;
  complexity: Complexity;
  isKidsAccount: boolean;
  parentEmail?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signup: (email: string, password: string) => Promise<any>;
  login: (email: string, password: string) => Promise<any>;
  logout: () => Promise<void>;
  signInWithGoogle: () => Promise<any>;
  initError: string | null;
}

export interface UserContextType {
  profile: KidsUserProfile | null;
  loading: boolean;
  setAge: (age: number) => Promise<void>;
  setComplexity: (complexity: Complexity) => Promise<void>;
  refreshProfile: () => Promise<void>;
  needsOnboarding: boolean;
}

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

// Grammar types
export interface GrammarExercise {
  id: string;
  sentence: string;
  complexity: Complexity;
}

export interface GrammarCheckResult {
  isCorrect: boolean;
  errors: Array<{
    word: string;
    suggestion: string;
    type: 'spelling' | 'grammar';
  }>;
  score: number;
  feedback: string;
}

// Reading types
export interface ReadingContent {
  id: string;
  title: string;
  content: string;
  questions: ReadingQuestion[];
  complexity: Complexity;
}

export interface ReadingQuestion {
  question: string;
  options: string[];
  correctIndex: number;
}

export interface ReadingCheckResult {
  score: number;
  correctAnswers: number[];
  feedback: string;
}

// Listening types
export interface ListeningContent {
  id: string;
  audioText: string;
  questions: ListeningQuestion[];
  complexity: Complexity;
}

export interface ListeningQuestion {
  question: string;
  options: string[];
  correctIndex: number;
}

// Speaking types
export interface SpeakingExercise {
  id: string;
  text: string;
  complexity: Complexity;
}

export interface SpeakingResult {
  accuracy: number;
  fluency: number;
  feedback: string;
  wordAnalysis: Array<{
    word: string;
    status: 'correct' | 'incorrect' | 'partial';
    score: number;
  }>;
}

// User Stats types
export interface KidsUserStats {
  totalPracticeMinutes: number;
  grammarSessions: number;
  readingSessions: number;
  listeningSessions: number;
  speakingSessions: number;
  averageGrammarScore: number;
  averageReadingScore: number;
  averageListeningScore: number;
  averageSpeakingScore: number;
  lastPracticeDate: Date | null;
  currentStreak: number;
  longestStreak: number;
  // Daily goal tracking
  dailyPracticeMinutes: number;
  lastDailyReset: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export type SessionType = 'grammar' | 'reading' | 'listening' | 'speaking';

// Daily goal constant
export const DAILY_GOAL_MINUTES = 15;

export interface SessionResult {
  sessionType: SessionType;
  durationMinutes: number;
  score?: number;
  dailyProgress: number; // 0-100 percentage
  dailyMinutes: number;
  goalReached: boolean;
}

export interface UserStatsContextType {
  stats: KidsUserStats | null;
  loading: boolean;
  error: string | null;
  dailyProgress: number; // 0-100 percentage
  dailyMinutes: number;
  goalReached: boolean;
  startSession: (type: SessionType) => void;
  endSession: (score?: number) => Promise<SessionResult | null>;
  refreshStats: () => Promise<void>;
}

// Subscription types
export type SubscriptionPlan = 'free' | 'kids';
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete';
export type PaymentProvider = 'stripe' | 'paypal' | null;
export type BillingCycle = 'monthly' | 'annual';

export interface Subscription {
  userId: string;
  userEmail: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  billingCycle: BillingCycle;
  provider: PaymentProvider;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  stripePriceId?: string;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  dailyRecordings: number;
  lastRecordingDate: Date | null;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface SubscriptionContextType {
  subscription: Subscription | null;
  plan: SubscriptionPlan;
  isActive: boolean;
  loading: boolean;
  error: string | null;
  createStripeCheckout: (successUrl: string, cancelUrl: string) => Promise<string>;
  createStripePortal: (returnUrl: string) => Promise<string>;
  refreshSubscription: () => Promise<void>;
  initializeSubscription: () => Promise<void>;
}

export const PLAN_NAMES: Record<SubscriptionPlan, string> = {
  free: 'Free',
  kids: 'Kids Premium',
};
